import { cubicOut } from 'svelte/easing';

/** Needle/fill travel when a light turns on or off. */
export const LIGHT_SLIDER_TWEEN_MS = 320;

export const LIGHT_SLIDER_EASING = cubicOut;

export type LightSliderTweenArgs = {
	from: number;
	to: number;
	primed: boolean;
	dragging: boolean;
};

/**
 * Shown slider value: the live reading while on or dragging, otherwise 0.
 */
export const lightSliderDisplayTarget = (isOn: boolean, value: number, isDragging: boolean): number =>
	isDragging || isOn ? value : 0;

/**
 * Animate only the off ↔ on jump. Drags, status nudges, and the first sync snap.
 */
export const shouldTweenLightSlider = ({ from, to, primed, dragging }: LightSliderTweenArgs): boolean =>
	primed && !dragging && from !== to && (from === 0 || to === 0);

export const lightSliderTweenDuration = (animate: boolean, reducedMotion: boolean): number =>
	animate && !reducedMotion ? LIGHT_SLIDER_TWEEN_MS : 0;

export const lightSliderListEquals = (from: number[], to: number[]): boolean => {
	if (from.length !== to.length) return false;
	for (let index = 0; index < from.length; index++) {
		if (from[index] !== to[index]) return false;
	}
	return true;
};

export const shouldTweenLightSliderList = (
	from: number[],
	to: number[],
	primed: boolean,
	dragging: boolean,
): boolean => {
	if (!primed || dragging || lightSliderListEquals(from, to)) return false;
	const length = Math.max(from.length, to.length);
	for (let index = 0; index < length; index++) {
		const start = from[index] ?? 0;
		const end = to[index] ?? 0;
		if (start !== end && (start === 0 || end === 0)) return true;
	}
	return false;
};

export const interpolateLightSliderList =
	(from: number[], to: number[]) =>
	(t: number): number[] => {
		const length = Math.max(from.length, to.length);
		const next: number[] = [];
		for (let index = 0; index < length; index++) {
			const start = from[index] ?? 0;
			const end = to[index] ?? 0;
			next.push(start + (end - start) * t);
		}
		return next;
	};

export type LightSliderHold =
	| { field: 'hue-sat'; hue: number; sat: number }
	| { field: 'ct'; value: number }
	| { field: 'brightness'; value: number }
	| { field: 'white'; value: number }
	| { field: 'mixer-brightness'; index: number; value: number };

/**
 * Status may overwrite a slider only when that field is not held from a
 * pointer commit still in flight.
 */
export const shouldApplyLightStatusField = (
	hold: LightSliderHold | undefined,
	field: LightSliderHold['field'],
	mixerIndex?: number,
): boolean => {
	if (!hold || hold.field !== field) return true;
	if (hold.field === 'mixer-brightness') return hold.index !== mixerIndex;
	return false;
};
